import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  Input,
  InputGroup,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { FormulaContainer } from 'packages/common-frontend/components/FormulaContainer';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { WipConfig } from 'packages/creditAgreements/ui/types';

type LenderPostgresQueryDateViewProps = {
  initialFieldValue: string;
  onEditOpen: () => void;
  evaluatedDate?: Util.FullDateStr;
  triggerDate?: Util.FullDateStr;
  dateType: 'startDate' | 'endDate' | 'reportingDate';
};
export const LenderPostgresQueryDateView = ({
  initialFieldValue,
  onEditOpen,
  evaluatedDate,
  triggerDate,
  dateType,
}: LenderPostgresQueryDateViewProps) => {
  return (
    <>
      <Flex width="100%" alignItems="center">
        <Box w="100%" mr="24px">
          <FormulaContainer>
            <Text fontWeight={500}>{initialFieldValue}</Text>
          </FormulaContainer>
        </Box>
        <EditIconButton label="Edit" ariaLabel="edit" onClick={onEditOpen} />
      </Flex>
      {!!evaluatedDate && !!triggerDate && (
        <Flex width="100%" alignItems="center" mt="16px" fontWeight={500}>
          <Text mr="8px">{`Based on the trigger date of ${commonDate.fullDateStrToHumanDateString(
            triggerDate,
          )}, the ${commonUtils.toTitleCase(dateType)} is`}</Text>
          <Text
            backgroundColor={colors.gray[200]}
            p="8px 16px"
            borderRadius="8px"
          >
            {commonDate.fullDateStrToHumanDateString(evaluatedDate)}
          </Text>
        </Flex>
      )}
    </>
  );
};

type PostgresQueryDateFormulaFieldEditProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  onSave: (
    query: CreditAgreement.Config.PortfolioCovenant.PostgresQuery.Query,
  ) => Promise<void>;
  isSaveLoading: boolean;
  postgresQuery:
    | CreditAgreement.Config.CommonPostgresQuery.Query.TAccountsSumQuery
    | CreditAgreement.Config.CommonPostgresQuery.Query.PrincipalBalanceDailyAverageQuery;
  formulaFieldKey: 'startDateFormula' | 'endDateFormula';
  triggerDate?: Util.FullDateStr;
  evaluatedDate?: Util.FullDateStr;
};
export const PostgresQueryDateFormulaField = ({
  wipConfig,
  fullPropertyKey,
  onSave,
  isSaveLoading,
  postgresQuery,
  formulaFieldKey,
  triggerDate,
  evaluatedDate,
}: PostgresQueryDateFormulaFieldEditProps) => {
  const toast = useToast();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const initialFieldValue =
    postgresQuery.parameterConfig[formulaFieldKey].formula;

  const [fieldValue, setFieldValue] = useState<string>(initialFieldValue);

  return (
    <Box
      p="32px"
      borderWidth="1px"
      borderRadius="8px"
      borderColor={colors.gray[200]}
    >
      {isEditOpen && wipConfig?.isMutateAllowed(fullPropertyKey) ? (
        <>
          <InputGroup mb="24px">
            <Input
              p="24px"
              type="text"
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
            />
          </InputGroup>
          <Flex width="100%" alignItems="center" justifyContent="flex-end">
            <Button
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={() => {
                setFieldValue(initialFieldValue);
                onEditClose();
              }}
              mr="24px"
            >
              Cancel
            </Button>
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isSaveLoading}
              onClick={async () => {
                try {
                  const updatedQuery = _.cloneDeep(postgresQuery);
                  updatedQuery.parameterConfig[
                    formulaFieldKey
                  ].formula = fieldValue;

                  await onSave(updatedQuery);
                } catch (err) {
                  toast({
                    ...errorToastConfig,
                    id: 'saveError',
                    title: 'Error saving formula. Please try again.',
                  });
                } finally {
                  onEditClose();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </>
      ) : (
        <LenderPostgresQueryDateView
          initialFieldValue={initialFieldValue}
          onEditOpen={onEditOpen}
          evaluatedDate={evaluatedDate}
          triggerDate={triggerDate}
          dateType={
            formulaFieldKey === 'startDateFormula' ? 'startDate' : 'endDate'
          }
        />
      )}
    </Box>
  );
};

export const PreviewPostgresQueryDrawerHeader = ({
  header,
  marginStyles,
}: {
  header: string;
  marginStyles?: { marginTop?: string; marginBottom?: string };
}) => {
  return (
    <>
      <Heading
        as="h4"
        fontSize="18px"
        lineHeight="26px"
        fontWeight={600}
        mt={marginStyles?.marginTop}
        mb={marginStyles?.marginBottom}
      >
        {header}
      </Heading>
      <Divider />
    </>
  );
};
